import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CreateChannelInput, PublicUser } from '@orbit-sync/shared-types';
import { useChatStore } from '../chat/chatStore.js';
import { errorMessage } from '../lib/api/index.js';
import { Icon } from './Icon.js';
import { PeoplePicker } from './PeoplePicker.js';

type Mode = 'direct' | 'group';

/**
 * "New conversation": pick people from the directory, then open a channel with
 * them. The list shows everyone straight away, alphabetically and loading more
 * as it scrolls; the search box narrows it.
 */
export function NewConversation({ onNavigate }: { onNavigate?: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rail-new">
      <button type="button" className="btn btn--primary btn--block" onClick={() => setOpen(true)}>
        <Icon name="plus" size={15} />
        New conversation
      </button>

      {/* Mounted only while open, so its state resets between uses. */}
      {open ? (
        <NewConversationDialog onClose={() => setOpen(false)} onNavigate={onNavigate} />
      ) : null}
    </div>
  );
}

function NewConversationDialog({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  /** Lets the shell close its mobile drawer, since this navigates on its own. */
  onNavigate?: (() => void) | undefined;
}) {
  const navigate = useNavigate();
  const createChannel = useChatStore((state) => state.createChannel);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<Mode>('direct');
  const [selected, setSelected] = useState<PublicUser[]>([]);
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // `showModal` rather than the `open` attribute: it is what gives the dialog a
  // focus trap, a backdrop and Esc-to-close. The explicit focus afterwards is
  // not redundant - `showModal` lands on the first focusable child (the close
  // button) - and this dialog exists to be typed into.
  useEffect(() => {
    dialogRef.current?.showModal();
    searchRef.current?.focus();
  }, []);

  async function openChannel(input: CreateChannelInput): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      const channel = await createChannel(input);

      onClose();
      onNavigate?.();
      await navigate(`/channels/${channel.id}`);
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }

  function choose(user: PublicUser): void {
    if (mode === 'direct') {
      // One click, no confirmation step: opening a DM is idempotent, so the
      // worst case is landing in a conversation that already existed.
      void openChannel({ type: 'direct', memberId: user.id });
      return;
    }

    setSelected((current) =>
      current.some((item) => item.id === user.id)
        ? current.filter((item) => item.id !== user.id)
        : [...current, user],
    );
  }

  const canCreateGroup = selected.length > 0 && groupName.trim().length > 0 && !busy;

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby="new-conversation-title"
      // Fires for Esc and for the close button alike, so every path unmounts.
      onClose={onClose}
    >
      <header className="modal-head">
        <div>
          <h2 id="new-conversation-title">New conversation</h2>
          <p>Pick a colleague from the list, or filter it by name.</p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="modal-scroll">
        <div className="mode-switch" role="tablist" aria-label="Conversation type">
          {(['direct', 'group'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              className={mode === value ? 'mode-tab mode-tab--active' : 'mode-tab'}
              onClick={() => {
                setMode(value);
                setSelected([]);
              }}
            >
              <Icon name={value === 'direct' ? 'messageSquare' : 'users'} size={14} />
              {value === 'direct' ? 'Direct message' : 'Group'}
            </button>
          ))}
        </div>

        {mode === 'group' ? (
          <div className="field">
            <label htmlFor="group-name">Group name</label>
            <input
              id="group-name"
              name="group-name"
              value={groupName}
              maxLength={120}
              placeholder="Platform team"
              onChange={(event) => setGroupName(event.target.value)}
            />
          </div>
        ) : null}

        {selected.length > 0 ? (
          <ul className="chips" aria-label="Selected people">
            {selected.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  className="chip"
                  onClick={() => choose(user)}
                  aria-label={`Remove ${user.displayName}`}
                >
                  {user.displayName}
                  <Icon name="x" size={13} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {error ? (
          <p className="field-error" role="alert">
            <Icon name="alertCircle" size={13} />
            {error}
          </p>
        ) : null}

        <PeoplePicker
          ref={searchRef}
          pickedIds={selected.map((user) => user.id)}
          disabled={busy}
          onChoose={choose}
          actionIcon={(picked) => (mode === 'direct' ? 'messageSquare' : picked ? 'check' : 'plus')}
          actionLabel={(_user, picked) =>
            mode === 'direct' ? 'Message' : picked ? 'Added' : 'Add'
          }
        />
      </div>

      {mode === 'group' ? (
        <footer className="modal-foot">
          <p className="muted small">
            {selected.length === 0
              ? 'Pick at least one person.'
              : `${selected.length} selected, plus you.`}
          </p>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canCreateGroup}
            onClick={() =>
              void openChannel({
                type: 'group',
                name: groupName.trim(),
                memberIds: selected.map((user) => user.id),
              })
            }
          >
            {busy ? <span className="btn-spinner" /> : <Icon name="users" size={14} />}
            {busy ? 'Creating…' : 'Create group'}
          </button>
        </footer>
      ) : null}
    </dialog>
  );
}
